import { useCallback, useEffect, useRef, useState } from 'react'
import type { ToolProject } from '../model'
import { loadProjects, saveProjects } from '../projectStore'

/** Trailing debounce for the localStorage write. Dragging a clip marker
 * mutates the project on every mousemove; serializing at 60 fps would be
 * pure waste, and losing the last 250 ms of a drag is impossible — the
 * drag ends before the delay does. */
const PERSIST_DELAY_MS = 250

export interface ProjectsApi {
  projects: ToolProject[]
  /** False for the first render, before localStorage is read — lets the
   * UI avoid flashing the empty state at someone who has projects. */
  ready: boolean
  addProject: (project: ToolProject) => void
  /** Apply a change to one project. The updater must return a new object;
   * ``updatedAt`` is stamped here so no call site has to remember it. */
  updateProject: (id: string, updater: (project: ToolProject) => ToolProject) => void
  removeProject: (id: string) => void
}

/**
 * Project list backed by localStorage.
 *
 * State is the source of truth during a session; persistence is a
 * debounced side effect, so a failed write never blocks an edit.
 */
export function useProjects(): ProjectsApi {
  const [projects, setProjects] = useState<ToolProject[]>([])
  const [ready, setReady] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    setProjects(loadProjects())
    setReady(true)
  }, [])

  // Persist on change, debounced. The cleanup flushes nothing on purpose:
  // the next scheduled write carries the latest state anyway, and the
  // unmount case is covered by the flush below.
  useEffect(() => {
    if (!ready) return
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = window.setTimeout(() => {
      saveProjects(projects)
      timerRef.current = null
    }, PERSIST_DELAY_MS)
  }, [projects, ready])

  // Unmount (route change, tab close) — write whatever is pending so a
  // clip marked one second ago isn't lost.
  const latestRef = useRef(projects)
  latestRef.current = projects
  const readyRef = useRef(ready)
  readyRef.current = ready
  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        if (readyRef.current) saveProjects(latestRef.current)
      }
    },
    [],
  )

  const addProject = useCallback((project: ToolProject) => {
    setProjects((prev) => [project, ...prev])
  }, [])

  const updateProject = useCallback(
    (id: string, updater: (project: ToolProject) => ToolProject) => {
      setProjects((prev) =>
        prev.map((p) => (p.id === id ? { ...updater(p), updatedAt: new Date().toISOString() } : p)),
      )
    },
    [],
  )

  const removeProject = useCallback((id: string) => {
    setProjects((prev) => prev.filter((p) => p.id !== id))
  }, [])

  return { projects, ready, addProject, updateProject, removeProject }
}
